'use client'

import { useLocale, useTranslations } from 'next-intl'

import { useSearchFilterStore } from '@/entities/map'
import {
  KeywordInput,
  PeriodPicker,
  useKeywordSearch,
} from '@/features/room-search'
import { usePathname, useRouter } from '@/shared/i18n'
import { cn } from '@/shared/lib'
import { Button } from '@/shared/ui'

type Props = {
  size?: 'default' | 'large'
}

export const KeywordSearchBar = ({ size = 'default' }: Props) => {
  const t = useTranslations('main')
  const locale = useLocale()

  const router = useRouter()
  const pathname = usePathname()

  const filterActions = useSearchFilterStore(state => state.actions)

  const search: Parameters<typeof useKeywordSearch>[0]['search'] = params => {
    if (pathname === '/guest/room') {
      const { startDate, endDate, keyword } = params

      filterActions.update({
        keyword: keyword,
        filters: { startDate, endDate },
      })

      return
    }

    const searchParams = new URLSearchParams()
    Object.entries(params).forEach(([key, value]) => {
      if (value) {
        searchParams.set(key, String(value))
      }
    })

    router.push(`/guest/room?${searchParams.toString()}`)
  }

  const { keywordInputProps, periodPickerProps, handleSubmit } =
    useKeywordSearch({
      search,
    })

  return (
    <div className="gradient-border shrink-0 shadow-[0px_-3px_7px_0px_rgba(0,0,0,0.05),0px_0px_7px_0px_rgba(0,0,0,0.05),0px_5px_6px_0px_rgba(0,0,0,0.11)]">
      <div
        className={cn(
          'flex h-[60px] w-[670px] items-center pr-4',
          size === 'large' && 'w-[704px]',
          size === 'default' && locale === 'en' && 'w-[710px]',
        )}
      >
        <div className="flex">
          <KeywordInput
            className={cn(
              'mr-1 w-[320px] shrink-0 pl-[5px]',
              size === 'large' && 'w-[330px]',
              size === 'default' && locale === 'en' && 'w-[346px]',
            )}
            inputClassName="bg-[rgba(0,0,0,0)] border-none w-full text-[15px]! leading-[22px]! h-[50px] *:placeholder:text-gray-400"
            placeholder={t('search_keyword_placeholder')}
            popupAlign="end"
            popupSideOffset={7}
            popupAlignOffset={size === 'large' ? -376 : -352}
            {...keywordInputProps}
          />
          <PeriodPicker
            className={cn(
              'mr-5 -ml-[5px] h-[50px] w-[266px] shrink-0 focus-visible:outline-none',
              size === 'large' && 'mr-9',
              locale === 'en' && 'w-[280px]',
            )}
            placeholder={t('search_date_placeholder')}
            align="end"
            sideOffset={7}
            alignOffset={size === 'large' ? -110 : -86}
            {...periodPickerProps}
          />
        </div>
        <Button
          size="small"
          weight="normal"
          className="w-[50px] shrink-0"
          onClick={handleSubmit}
        >
          {t('search.button')}
        </Button>
      </div>
    </div>
  )
}
